import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { fonts, StatusKey, StatusToken } from '@/theme';
import { useModeTokens } from './useModeTokens';

interface StatusPillProps {
  children?: React.ReactNode;
  /** Which state this pill reports. Drives all three colours. */
  status?: StatusKey;
  /** Escape hatch for a one-off tone not in the status set. */
  tone?: StatusToken;
  /** Show the leading status dot. */
  dot?: boolean;
  size?: number;
  /** Render without a border — used for compact card tags. */
  borderless?: boolean;
  /** Pin to one mode instead of following the theme. */
  light?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Every state chip — SYNCED, CONFLICT, BEHIND 2, PENDING, STEM PACK,
 * LOCAL NEWER. One component, so a status can never be reported in two
 * different colours on two different screens.
 */
export const StatusPill: React.FC<StatusPillProps> = ({
  children,
  status = 'neutral',
  tone,
  dot = false,
  size = 9.5,
  borderless = false,
  light,
  sx,
}) => {
  // Resolved before the branch — `??` would short-circuit the hook away.
  const modeTokens = useModeTokens(light);
  const t = tone ?? modeTokens.status[status];

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        padding: borderless ? '3px 7px' : '4px 9px',
        borderRadius: '999px',
        background: t.bg,
        border: borderless ? 0 : `1px solid ${t.border}`,
        color: t.fg,
        fontFamily: fonts.mono,
        fontWeight: 600,
        fontSize: `${size}px`,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
        flex: 'none',
        ...sx,
      }}
    >
      {dot && (
        <Box
          component="span"
          sx={{
            width: 5,
            height: 5,
            borderRadius: '999px',
            background: t.fg,
            flex: 'none',
          }}
        />
      )}
      {children}
    </Box>
  );
};

export default StatusPill;
